import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Search } from "lucide-react";
import { frApi, errMsg, cardCls, fieldCls, labelCls, btnPrimary, btnGhost, TIER_CLS } from "./frUtils";

const EMPTY = { name: "", tier: 4, aliases: "", state: "" };

function CityForm({ init, onSubmit, onCancel, busy }) {
    const [f, setF] = useState(init);
    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, tier: Number(f.tier), aliases: f.aliases.split(",").map((s) => s.trim()).filter(Boolean) }); }}
            className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_2fr_auto] gap-2 items-end" data-testid="fr-city-form">
            <div className="col-span-2 sm:col-span-1"><label className={labelCls}>City</label><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={fieldCls} data-testid="fr-city-form-name" required /></div>
            <div><label className={labelCls}>Tier</label>
                <select value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} className={fieldCls} data-testid="fr-city-form-tier">{[1, 2, 3, 4].map((t) => <option key={t} value={t}>Tier {t}</option>)}</select></div>
            <div><label className={labelCls}>Other spellings (comma separated)</label><input value={f.aliases} onChange={(e) => setF({ ...f, aliases: e.target.value })} className={fieldCls} data-testid="fr-city-form-aliases" placeholder="e.g. Bangalore" /></div>
            <div className="col-span-2 sm:col-span-1 flex gap-2">
                <button type="submit" disabled={busy} className={btnPrimary} data-testid="fr-city-form-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
                {onCancel && <button type="button" onClick={onCancel} className={btnGhost}>Cancel</button>}
            </div>
        </form>
    );
}

export default function CitiesTab() {
    const [items, setItems] = useState(null);
    const [q, setQ] = useState("");
    const [tier, setTier] = useState(0);
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => frApi.get("/cities").then((d) => setItems(d.items)).catch((e) => toast.error(errMsg(e))), []);
    useEffect(() => { load(); }, [load]);
    const submit = async (body, id) => {
        setBusy(true);
        try { await (id ? frApi.put(`/cities/${id}`, body) : frApi.post("/cities", body)); toast.success("City saved"); setEditing(null); setAdding(false); load(); }
        catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };
    const remove = async (c) => {
        if (!window.confirm(`Remove ${c.name}? It will fall back to Tier 4.`)) return;
        try { await frApi.del(`/cities/${c.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(errMsg(e)); }
    };
    if (!items) return <div className="grid place-items-center p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
    const ql = q.trim().toLowerCase();
    const shown = items.filter((c) => (!tier || c.tier === tier) && (!ql || c.name.toLowerCase().includes(ql) || (c.aliases || []).some((a) => a.toLowerCase().includes(ql))));

    return (
        <div className="space-y-4" data-testid="fr-cities">
            <div className={`${cardCls} space-y-3`}>
                <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city or spelling" className={`${fieldCls} pl-9`} data-testid="fr-cities-search" /></div>
                    <button type="button" onClick={() => setAdding((v) => !v)} className={btnPrimary} data-testid="fr-cities-add-btn"><Plus className="w-4 h-4" /> Add city</button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {[0, 1, 2, 3, 4].map((t) => (
                        <button key={t} type="button" onClick={() => setTier(t)} data-testid={`fr-cities-tier-${t}`}
                            className={`h-9 px-3 rounded-full text-xs font-semibold border ${tier === t ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-700 border-slate-200"}`}>
                            {t ? `Tier ${t} (${items.filter((c) => c.tier === t).length})` : `All (${items.length})`}
                        </button>
                    ))}
                </div>
                <p className="text-xs text-slate-500">Any Indian city not listed here is treated as Tier 4.</p>
                {adding && <CityForm init={EMPTY} busy={busy} onSubmit={(b) => submit(b)} onCancel={() => setAdding(false)} />}
            </div>
            <div className={`${cardCls} !p-0 divide-y divide-slate-100`}>
                {shown.length === 0 && <div className="p-4 text-sm text-slate-500" data-testid="fr-cities-empty">No matching city. Unlisted cities default to Tier 4.</div>}
                {shown.map((c) => (
                    <div key={c.id} className="p-3 sm:px-4" data-testid={`fr-city-row-${c.key}`}>
                        {editing === c.id ? (
                            <CityForm init={{ name: c.name, tier: c.tier, aliases: (c.aliases || []).join(", "), state: c.state || "" }} busy={busy} onSubmit={(b) => submit(b, c.id)} onCancel={() => setEditing(null)} />
                        ) : (
                            <div className="flex items-center gap-3">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${TIER_CLS[c.tier]}`}>T{c.tier}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm text-[#0F172A]">{c.name}{c.manual && <span className="ml-1.5 text-[10px] text-blue-700 font-bold">EDITED</span>}</div>
                                    {c.aliases?.length > 0 && <div className="text-xs text-slate-500 truncate">Also: {c.aliases.join(", ")}</div>}
                                </div>
                                <button type="button" onClick={() => setEditing(c.id)} aria-label={`Edit ${c.name}`} className="h-10 w-10 grid place-items-center text-slate-500 hover:text-[#1E3A8A]" data-testid={`fr-city-edit-${c.key}`}><Pencil className="w-4 h-4" /></button>
                                <button type="button" onClick={() => remove(c)} aria-label={`Remove ${c.name}`} className="h-10 w-10 grid place-items-center text-slate-500 hover:text-rose-600" data-testid={`fr-city-delete-${c.key}`}><Trash2 className="w-4 h-4" /></button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
